import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useMemo, useState } from 'react';
import {
  complianceDeadlines as api, companies as companiesApi, users as usersApi,
  type ComplianceDeadlineInput, type AssignableUser,
} from '@/api/endpoints';
import type { ComplianceDeadline, ComplianceCategory, ComplianceStatus, Company } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

const CATEGORIES: { id: ComplianceCategory; name: string }[] = [
  { id: 'INSPECTION', name: 'Przegląd' },
  { id: 'BHP', name: 'BHP' },
  { id: 'INSURANCE', name: 'Ubezpieczenie' },
  { id: 'WATER_MONITORING', name: 'Monitoring wody' },
  { id: 'OTHER', name: 'Inne' },
];

const CAT_PILL: Record<ComplianceCategory, string> = {
  INSPECTION: 'bg-water-100 text-water-700',
  BHP: 'bg-amber-100 text-amber-700',
  INSURANCE: 'bg-ai-100 text-ai-600',
  WATER_MONITORING: 'bg-water-100 text-water-700',
  OTHER: 'bg-ink-100 text-ink-600',
};

const pluralDni = (n: number) => (n === 1 ? 'dzień' : 'dni');

/** Etykieta „ile dni do końca / po terminie". */
function daysLabel(d: ComplianceDeadline): string {
  if (d.status !== 'ACTIVE' || d.daysUntil == null) return '';
  const n = d.daysUntil;
  if (n === 0) return 'dziś';
  if (n > 0) return `za ${n} ${pluralDni(n)}`;
  return `${-n} ${pluralDni(-n)} po terminie`;
}

function urgencyColor(u: ComplianceDeadline['urgency']): string {
  if (u === 'OVERDUE') return 'text-red-500 font-semibold';
  if (u === 'DUE_SOON') return 'text-amber-500 font-semibold';
  return 'text-ink-600';
}

function scopeLabel(d: ComplianceDeadline): string {
  const parts: string[] = [];
  if (d.companyName) parts.push('Firma: ' + d.companyName);
  if (d.userName) parts.push('Pracownik: ' + d.userName);
  return parts.join(' · ') || '—';
}

function recurrenceLabel(months?: number | null): string {
  if (!months) return 'jednorazowy';
  if (months === 12) return 'co rok';
  if (months % 12 === 0) return `co ${months / 12} lata/lat`;
  return `co ${months} mies.`;
}

export function TerminyPage() {
  const [items, setItems] = useState<ComplianceDeadline[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ComplianceStatus>('ACTIVE');
  const [catFilter, setCatFilter] = useState<ComplianceCategory | ''>('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [people, setPeople] = useState<AssignableUser[]>([]);
  const [modalItem, setModalItem] = useState<ComplianceDeadline | 'new' | null>(null);

  const reload = () => {
    setLoading(true);
    api.list({ status: statusFilter, category: catFilter || undefined })
      .then(setItems)
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [statusFilter, catFilter]);
  useEffect(() => {
    companiesApi.list().then(setCompanies).catch(() => {});
    usersApi.assignable().then(setPeople).catch(() => {});
  }, []);

  const overdueCount = useMemo(() => items.filter(i => i.urgency === 'OVERDUE').length, [items]);
  const dueSoonCount = useMemo(() => items.filter(i => i.urgency === 'DUE_SOON').length, [items]);

  const onComplete = async (d: ComplianceDeadline) => {
    if (!confirm(`Oznaczyć „${d.title}" jako wykonane dziś?` +
      (d.recurrenceMonths ? `\nKolejny termin powstanie za ${d.recurrenceMonths} mies.` : ''))) return;
    try { await api.complete(d.id); reload(); }
    catch (e) { alert('Błąd: ' + (e instanceof Error ? e.message : String(e))); }
  };
  const onDelete = async (d: ComplianceDeadline) => {
    if (!confirm(`Usunąć termin „${d.title}"? Tej operacji nie można cofnąć.`)) return;
    try { await api.remove(d.id); reload(); }
    catch (e) { alert('Błąd: ' + (e instanceof Error ? e.message : String(e))); }
  };

  return (
    <div className="max-w-[1200px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Terminy</h1>
          <div className="text-ink-400 text-[13px]">Przeglądy, BHP, ubezpieczenia i inne terminy do pilnowania</div>
        </div>
        <button onClick={() => setModalItem('new')} className="btn btn-primary btn-sm">
          <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj termin
        </button>
      </header>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex gap-1">
          {(['ACTIVE', 'DONE', 'CANCELLED'] as ComplianceStatus[]).map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={'btn btn-sm ' + (statusFilter === s ? 'btn-primary' : 'btn-ghost')}>
              {s === 'ACTIVE' ? 'Aktywne' : s === 'DONE' ? 'Wykonane' : 'Odwołane'}
            </button>
          ))}
        </div>
        <div className="w-px h-5 bg-ink-200 mx-1"/>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setCatFilter('')}
            className={'btn btn-sm ' + (catFilter === '' ? 'btn-primary' : 'btn-ghost')}>Wszystkie</button>
          {CATEGORIES.map(c => (
            <button key={c.id} onClick={() => setCatFilter(c.id)}
              className={'btn btn-sm ' + (catFilter === c.id ? 'btn-primary' : 'btn-ghost')}>{c.name}</button>
          ))}
        </div>
        {statusFilter === 'ACTIVE' && (overdueCount > 0 || dueSoonCount > 0) && (
          <div className="ml-auto flex items-center gap-2 text-[12px]">
            {overdueCount > 0 && <span className="pill pill-red">{overdueCount} po terminie</span>}
            {dueSoonCount > 0 && <span className="pill" style={{ background: '#fef3c7', color: '#b45309' }}>{dueSoonCount} wkrótce</span>}
          </div>
        )}
      </div>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}
      {loading && <div className="text-ink-400">Ładowanie…</div>}

      {!loading && items.length === 0 && (
        <div className="card"><div className="card-body text-center text-ink-400 py-8">
          Brak terminów w tym widoku. Kliknij „Dodaj termin", aby wprowadzić pierwszy.
        </div></div>
      )}

      {!loading && items.length > 0 && (
        <div className="card">
          <div className="card-body !p-0">
            <table className="table-hw">
              <thead>
                <tr>
                  <th className="w-28">Rodzaj</th>
                  <th>Tytuł / zakres</th>
                  <th className="w-32">Termin</th>
                  <th className="w-28">Cykl</th>
                  <th className="w-40"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(d => (
                  <tr key={d.id}>
                    <td>
                      <span className={'inline-block px-2 py-0.5 rounded text-[11px] font-semibold ' + CAT_PILL[d.category]}>
                        {d.categoryName}
                      </span>
                    </td>
                    <td>
                      <div className="font-semibold text-ink-900">{d.title}</div>
                      <div className="text-[11px] text-ink-400">{scopeLabel(d)}</div>
                      {d.description && <div className="text-[11px] text-ink-500 mt-0.5 line-clamp-2">{d.description}</div>}
                    </td>
                    <td className={'font-mono text-[12px] ' + urgencyColor(d.urgency)}>
                      <div className="flex flex-col leading-tight">
                        <span>{d.dueDate}</span>
                        {d.status === 'ACTIVE' && <span className="text-[10px] font-sans">{daysLabel(d)}</span>}
                        {d.status !== 'ACTIVE' && <span className="text-[10px] font-sans text-ink-400">{d.statusName}</span>}
                      </div>
                    </td>
                    <td className="text-[12px] text-ink-500">{recurrenceLabel(d.recurrenceMonths)}</td>
                    <td>
                      <div className="flex items-center gap-1 justify-end">
                        {d.status === 'ACTIVE' && (
                          <button onClick={() => onComplete(d)} className="btn btn-ghost btn-sm text-green-600 hover:bg-green-50" title="Oznacz wykonane">
                            <Icon name="check" className="w-3.5 h-3.5"/> Wykonane
                          </button>
                        )}
                        <button onClick={() => setModalItem(d)} className="btn btn-ghost btn-icon text-ink-400 hover:text-water-600" title="Edytuj">
                          <Icon name="edit" className="w-3.5 h-3.5"/>
                        </button>
                        <button onClick={() => onDelete(d)} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500" title="Usuń">
                          <Icon name="x" className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalItem && (
        <DeadlineModal
          item={modalItem === 'new' ? null : modalItem}
          companies={companies}
          people={people}
          onClose={() => setModalItem(null)}
          onSaved={() => { setModalItem(null); reload(); }}
        />
      )}
    </div>
  );
}

function DeadlineModal({ item, companies, people, onClose, onSaved }: {
  item: ComplianceDeadline | null;
  companies: Company[];
  people: AssignableUser[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!item;
  const [category, setCategory] = useState<ComplianceCategory>(item?.category ?? 'INSPECTION');
  const [title, setTitle] = useState(item?.title ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [companyId, setCompanyId] = useState<number | ''>(item?.companyId ?? '');
  const [userId, setUserId] = useState<number | ''>(item?.userId ?? '');
  const [responsibleUserId, setResponsibleUserId] = useState<number | ''>(item?.responsibleUserId ?? '');
  const [dueDate, setDueDate] = useState(item?.dueDate ?? new Date().toISOString().slice(0, 10));
  const [recurrenceMonths, setRecurrenceMonths] = useState<number | ''>(item?.recurrenceMonths ?? '');
  const [remindDaysBefore, setRemindDaysBefore] = useState<number>(item?.remindDaysBefore ?? 30);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setErr('Tytuł jest wymagany'); return; }
    if (!dueDate) { setErr('Termin (data) jest wymagany'); return; }
    setBusy(true); setErr(null);
    const payload: ComplianceDeadlineInput = {
      category,
      title: title.trim(),
      description: description.trim() || null,
      companyId: companyId === '' ? null : Number(companyId),
      userId: userId === '' ? null : Number(userId),
      responsibleUserId: responsibleUserId === '' ? null : Number(responsibleUserId),
      dueDate,
      recurrenceMonths: recurrenceMonths === '' ? null : Number(recurrenceMonths),
      remindDaysBefore: Number(remindDaysBefore) || 30,
    };
    try {
      if (isEdit) await api.update(item!.id, payload);
      else await api.create(payload);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { category, title, description, companyId, userId, responsibleUserId, dueDate, recurrenceMonths, remindDaysBefore }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-lg shadow-xl w-full max-w-[640px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100">
          <h2 className="text-[15px] font-semibold text-ink-900">{isEdit ? 'Edytuj termin' : 'Nowy termin'}</h2>
        </div>
        <div className="p-5 grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Rodzaj *</span>
              <SearchableSelect value={category} onChange={v => setCategory(v as ComplianceCategory)}>
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SearchableSelect>
            </label>
            <label className="field">
              <span className="field-label">Termin (data) *</span>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="input"/>
            </label>
          </div>

          <label className="field">
            <span className="field-label">Tytuł *</span>
            <input value={title} onChange={e => setTitle(e.target.value)} className="input"
                   placeholder="np. Ubezpieczenie OC firmy / Szkolenie BHP okresowe"/>
          </label>

          <label className="field">
            <span className="field-label">Opis (opcjonalnie)</span>
            <textarea value={description} onChange={e => setDescription(e.target.value)} className="textarea" rows={2}/>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Firma (opcjonalnie)</span>
              <SearchableSelect value={companyId === '' ? '' : String(companyId)} onChange={v => setCompanyId(v ? Number(v) : '')}>
                <option value="">— brak —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.shortName}</option>)}
              </SearchableSelect>
            </label>
            <label className="field">
              <span className="field-label">Pracownik (opcjonalnie)</span>
              <SearchableSelect value={userId === '' ? '' : String(userId)} onChange={v => setUserId(v ? Number(v) : '')}>
                <option value="">— brak —</option>
                {people.map(p => <option key={p.id} value={p.id}>{p.fullName}</option>)}
              </SearchableSelect>
            </label>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="field">
              <span className="field-label">Powtarzaj co (mies.)</span>
              <input type="number" min="0" step="1" value={recurrenceMonths}
                     onChange={e => setRecurrenceMonths(e.target.value === '' ? '' : Number(e.target.value))}
                     className="input" placeholder="puste = jednorazowy"/>
            </label>
            <label className="field">
              <span className="field-label">Przypominaj (dni przed)</span>
              <input type="number" min="0" step="1" value={remindDaysBefore}
                     onChange={e => setRemindDaysBefore(Number(e.target.value))} className="input"/>
            </label>
            <label className="field">
              <span className="field-label">Odpowiedzialny</span>
              <SearchableSelect value={responsibleUserId === '' ? '' : String(responsibleUserId)}
                                onChange={v => setResponsibleUserId(v ? Number(v) : '')}>
                <option value="">— właściciele —</option>
                {people.map(p => <option key={p.id} value={p.id}>{p.fullName}</option>)}
              </SearchableSelect>
            </label>
          </div>
          <div className="text-[11px] text-ink-400 -mt-1">
            Cykliczny: po odhaczeniu system założy kolejny termin. Powiadomienia + e-mail trafią do „odpowiedzialnego"
            (lub pracownika, a domyślnie do właścicieli) na {remindDaysBefore} dni przed i po terminie.
          </div>

          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}

          <div className="flex justify-end gap-2 mt-1">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy} className="btn btn-primary">
              <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : (isEdit ? 'Zapisz' : 'Dodaj termin')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
